import {
  ActiveOrdersContext,
  ActiveOrdersContextType,
} from '@/contexts/activeOrders/useActiveOrdersContext'
import useOrdersWebSocket from '@/contexts/ordersWebSocket/useOrdersWebSocket'
import { useActiveOrders } from '@/proxy/order/hooks'
import { Routes } from '@/resources/enums/enumRoutes'
import { ActiveOrder } from '@/resources/types'
import { CHECK_ORDERS_POLLING_TIME } from '@/seller.application.polling.time.config'
import { PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/** graphql-request attaches HTTP status on `error.response.status` (same shape as ClientError). */
const getGraphqlRequestHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined
  }
  const response = (error as { response?: { status?: number } }).response
  return response?.status
}

const ActiveOrdersProvider = ({ children }: PropsWithChildren) => {
  const { orders, isActive, hasUpdatedOrders } = useOrdersWebSocket()
  const prevIsActive = useRef<boolean>(false)
  const isQueryDisabled = isActive && hasUpdatedOrders
  const { pathname } = useLocation()
  const isActiveOrdersRoute =
    pathname === (Routes.ORDERS as string) ||
    pathname === (Routes.ORDERS_MAP as string)

  const {
    isFetching: isQueryFetching,
    isFetched: isQueryFetched,
    data: { activeOrders: queryOrders = [], error: queryError } = {},
    error: queryRequestError,
    isError: isQueryRequestError,
    refetch,
  } = useActiveOrders(
    {
      gcTime: 0,
      refetchInterval: isActive ? false : CHECK_ORDERS_POLLING_TIME,
      refetchIntervalInBackground: true,
      enabled: !isQueryDisabled,
    },
    !isActiveOrdersRoute
  )

  useEffect(() => {
    if (!isActive && isActive !== prevIsActive.current) void refetch()
    prevIsActive.current = isActive
  }, [isActive, refetch])

  const activeOrders: ActiveOrder[] = useMemo(
    () => (isActive && hasUpdatedOrders ? orders : queryOrders),
    [isActive, hasUpdatedOrders, orders, queryOrders]
  )

  const httpStatus = getGraphqlRequestHttpStatus(queryRequestError)
  const isActiveOrdersQueryError =
    isQueryRequestError && (httpStatus === 500 || httpStatus === 503)

  const context: ActiveOrdersContextType = useMemo(
    () => ({
      activeOrders,
      isPollingActive: !isActive,
      isFromWss: isQueryDisabled,
      isQueryFetching,
      isFetched: isQueryFetched || hasUpdatedOrders,
      queryError,
      isActiveOrdersQueryError,
    }),
    [
      activeOrders,
      isActive,
      isQueryDisabled,
      isQueryFetching,
      queryError,
      isQueryFetched,
      hasUpdatedOrders,
      isActiveOrdersQueryError,
    ]
  )

  return (
    <ActiveOrdersContext.Provider value={context}>
      {children}
    </ActiveOrdersContext.Provider>
  )
}

export default ActiveOrdersProvider
